import { useState } from 'react';
import {
  IndeedThemeProvider,
  Button,
  ButtonLink,
  Box,
  Heading,
  Text,
} from '@indeed/ifl-components';
import { Information, NewWindow } from '@indeed/ifl-icons';
import { Modal, ModalBody, ModalFooter } from '@indeed/ipl-modal';
import { colors } from '@indeed/ifl-primitives';
import TextStat from './components/textStat';
import AvgSalary from './components/avgSalary';
import SearchTerms from './components/searchTerms';
import { i18nDateFormat } from './utils/localizationUtils';
import { Localization } from './types';
import { getCurrency } from 'locale-currency';

interface AppProps {
  title: string;
  displayTitle?: string;
  modalData: {
    sections: Record<string, any>[];
    ctas: Record<string, any>[];
    sourceName: string;
    sourceDate: string;
    modalTitleSuffix: string;
    localization: Localization;
  };
}

const App: React.FC<AppProps> = ({
  title,
  displayTitle,
  modalData: {
    sourceName,
    sourceDate,
    sections,
    ctas,
    localization,
    modalTitleSuffix,
  },
}) => {
  const [isOpen, setOpen] = useState(false);
  const handleOnExit = () => {
    setOpen(false);
  };

  if (!title && !displayTitle) {
    // renders nothing when titles are empty
    return <></>;
  } else if (!sections || !sections.length) {
    // renders title as plain text when none of the sections has data
    return <Text>{displayTitle || title}</Text>;
  } else {
    // renders the modal
    // @ts-ignore
      return (
      <IndeedThemeProvider>
        <Button
          type="button"
          onClick={() => setOpen(true)}
          variant="text"
          iconAfter={<Information aria-hidden size="sm" sx={{ ml: 1 }} />}
          sx={{
              fontWeight: 'inherit',
              fontSize: 'inherit'
            }}
        >
            <Text as="span"
                  sx={{
                      color: colors.primary["800"],
                      minBlockSize: 'auto',
                      p: 0,
                      borderRadius: 0,
                      border: 0,
                      borderBottom: 'solid 1px',
                      borderColor: colors.primary["800"],
                      ':hover:not([data-focus-visible-added]), :focus:not([data-focus-visible-added])': {
                          borderBottom: 'solid 1px',
                          borderColor: 'transparent', // having a transparent 1px placeholder bottom border prevents element shift when border appears or disappears
                          color: colors.primary["900"],
                      },
                  }}>{displayTitle || title}</Text>
        </Button>
        <Modal
          isOpen={isOpen}
          onExit={handleOnExit}
          ariaLabel={displayTitle || title}
          title={
            <Heading as="h2" level={5} sx={{ textTransform: 'capitalize' }}>
              {displayTitle || title}: {modalTitleSuffix}
            </Heading>
          }
          subtitle={
            <Text level={1} sx={{ mt: 4 }}>
              {sourceName} - {i18nDateFormat(sourceDate, localization.locale)}
            </Text>
          }
          sx={{ maxHeight: '70vh' }}
        >
          <ModalBody
            sx={{
              // Top and bottom inset shadow to indicate scrollability
              // Disabling @indeed/uxqual/use-shadow-theme-values in eslintec because there is no shadow token for this custom shadow.
              boxShadow:
                'inset 0 10px 10px -10px rgba(0, 0, 0, 0.2), inset 0 -10px 10px -10px rgba(0, 0, 0, 0.2)',
            }}
          >
            {sections.map((component) => (
                component && <Box
                key={component.name}
                sx={{
                  borderBottom: 'solid 1px',
                  borderColor: 'border.weak',
                  pt: 6,
                  pb: 6,
                  px: 3,
                  position: 'relative',
                  ':first-of-type': {
                    pt: 0,
                    // Hides inset shadow when scrolled to top
                    '::before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: '-1.5rem',
                      left: '-1.5rem',
                      width: `calc(100% + 3rem)`,
                      height: '30px',
                      background: `linear-gradient(180deg,  ${colors.neutral[0]} 15%, transparent 100%);`,
                    },
                  },
                  ':last-of-type': {
                    pb: 0,
                    borderBottom: 0,
                    // Hides inset shadow when scrolled to bottom
                    '::after': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      bottom: '-2rem',
                      left: '-1.5rem',
                      width: `calc(100% + 3rem)`,
                      height: '30px',
                      background: `linear-gradient(0deg, ${colors.neutral[0]} 15%, transparent 100%);`,
                    },
                  },
                }}
              >
                {(component.name === 'job_seekers' ||
                  component.name === 'employers' ||
                  component.name === 'resumes') && (
                  <TextStat
                    label={component.label}
                    amount={parseInt(component.amount)}
                    change={
                      component.change_amount
                        ? parseInt(component.change_amount)
                        : undefined
                    }
                    date={component.date}
                    description={component.description}
                    info={component.tooltip_info}
                    localization={localization}
                  />
                )}
                {component.name === 'average_salary' && (
                  <AvgSalary
                    label={component.label}
                    avgRate={parseFloat(component.average)}
                    unit={component.unit}
                    minRate={parseFloat(component.low)}
                    maxRate={parseFloat(component.high)}
                    info={component.tooltip_info}
                    currencyCode={localization.currency || getCurrency(localization.locale)}
                    localization={localization}
                  />
                )}
                {component.name === 'top_5_search_terms' && (
                  <SearchTerms
                    label={component.label}
                    info={component.tooltip_info}
                    terms={component.terms || []}
                    percentStrings={component.percentStrings || []}
                    locale={localization.locale}
                  />
                )}
              </Box>
            ))}
          </ModalBody>
          <ModalFooter sx={{ display: 'flex' }}>
            {ctas.map(
              (cta) =>
                cta.show && (
                  <ButtonLink
                    target="_blank"
                    href={cta.url}
                    iconAfter={<NewWindow size="xs" aria-hidden />}
                    data-tn-link=""
                    data-tn-element="job-title-modal-cta"
                    sx={{
                      // Override style from the theme that turns links blue on focus.
                      [':focus']: {
                        color: 'foreground.inverse',
                      },
                      ':last-child': {
                        ml: 'auto',
                      },
                    }}
                    key={cta.text}
                  >
                    {cta.text}
                  </ButtonLink>
                )
            )}
          </ModalFooter>
        </Modal>
      </IndeedThemeProvider>
    );
  }
};

export default App;
